import { FormControl as MuiFormControl, Stack, Typography } from '@mui/material';
import { validateSmartyAddress } from 'api/smarty/smarty.requests';
import {
  addManuallyEnteredLocationExposure,
  updateManuallyEnteredLocationExposure,
  useSubmission,
} from 'api/submission';
import { DEFAULT_TOGGLE_BUTTONS_GROUP_OPTIONS, STATES } from 'common/constants';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { ExposuresWrapper } from 'components/ExposuresWrapper';
import { FormControl } from 'components/lib/FormControl';
import { SmartyAutocompleteInput } from 'components/lib/SmartyAutocompleteInput';
import type { SmartyAutoCompleteInputRef } from 'components/lib/SmartyAutocompleteInput/SmartyAutocompleteInput.types';
import { ToggleButtonsGroup } from 'components/lib/ToggleButtonsGroup';
import { useFormik } from 'formik';
import { parseLocation } from 'helpers/utils';
import { useExposuresOrGroupFields } from 'hooks/useExposuresOrGroupFields';
import { isEmpty, omit } from 'lodash-es';
import { type FC, type MouseEvent, useEffect, useRef, useState } from 'react';
import { useFullscreenLoader } from 'store/features/UI';
import * as yup from 'yup';
import type {
  DestructuredAddress,
  ILocation,
  ManuallyEnteredAddressPayload,
} from './Location.types';

const validationSchema = yup.object({
  loc_address: yup.string().required(),
  loc_address_same_as_mailing_address: yup.string().required(),
  mail_address: yup.string().when('loc_address_same_as_mailing_address', {
    is: (loc_address_same_as_mailing_address: string) =>
      loc_address_same_as_mailing_address === 'No',
    then: () => yup.string().required(),
    otherwise: () => yup.string().notRequired(),
  }),
});

const Location: FC = () => {
  const { submission, submissionId, relatedResource, performAction } =
    useExposuresOrGroupFields<ILocation>({
      type: 'exposure',
      name: 'location',
    });

  const { refetch: refetchSubmission } = useSubmission(submissionId);

  const { setFullscreenLoading } = useFullscreenLoader();

  const smartyAutoCompleteInputRefs = useRef<{ [key: string]: SmartyAutoCompleteInputRef }>({});

  const [state, setState] = useState<ILocation>({
    loc_address: '',
    loc_address_same_as_mailing_address: '',
    mail_address: '',
  });

  const formik = useFormik({
    initialValues: {
      ...state,
    },
    validationSchema,
    onSubmit: (_values: ILocation) => {},
  });

  const locationExposure = relatedResource?.[0];

  useEffect(() => {
    if (!isEmpty(submission?.data)) {
      setState(locationExposure?.data || {});
      formik.setValues(locationExposure?.data || {});
    }
  }, [submission?.data]);

  const showLocationAddressInput = formik.values.loc_address_same_as_mailing_address === 'No';

  const updateField = (fieldName: string, newValue: string) => {
    setState((prevValue) => ({ ...prevValue, [fieldName]: newValue }));
    formik.setFieldValue(fieldName, newValue);
  };

  const handleOnChangeToggleButtonGroups = (_: MouseEvent<HTMLElement>, newValue: string) => {
    updateField('loc_address_same_as_mailing_address', newValue);
  };

  useEffect(() => {
    if (!showLocationAddressInput && formik.dirty) {
      updateField('mail_address', '');
      updateField('mail_address_destructured', '');
    }
  }, [showLocationAddressInput, formik.dirty]);

  const validateAddress = async (addressInfo: DestructuredAddress) => {
    try {
      const missingAddressParts: string[] = [];

      Object.entries(omit(addressInfo, 'secondary')).forEach(([key, value]) => {
        if (isEmpty(value)) {
          missingAddressParts.push(key);
        }
      });

      const isAddressValid = missingAddressParts.length === 0;

      if (!isAddressValid) {
        throw Error(`Address is invalid, missing parts: ${missingAddressParts.join(', ')}`);
      }

      const res = await validateSmartyAddress({
        street: addressInfo.street_line,
        street2: addressInfo.secondary,
        city: addressInfo.city,
        zipcode: addressInfo.zipcode,
        state: addressInfo.state,
        secondary: addressInfo.secondary,
      });

      const validatedAddress = res?.metadata;

      const latitude = validatedAddress?.latitude;
      const longitude = validatedAddress?.longitude;

      if (!(latitude && longitude)) {
        throw Error(
          'Latitude or Longitude can not be found. Please review the address and try again.',
        );
      }

      return { latitude, longitude };
    } catch (error) {
      const e = error as Error;

      throw e;
    }
  };

  // eslint-disable-next-line complexity
  const handleNext = async () => {
    let isAnyManualInputAddressHasErrors = false;

    let isAnyManualInputAddressEntered = false;

    const manualAddressInputValidations: Promise<boolean>[] = [];

    for (const [fieldName, ref] of Object.entries(smartyAutoCompleteInputRefs.current)) {
      if (ref) {
        const isManualAddressInputActive = ref.getIsManualAddressInputActive();

        if (isManualAddressInputActive) {
          manualAddressInputValidations.push(ref.validateManualAddressInput() as Promise<boolean>);
        } else {
          const addressInputState = ref.getAddressInputState();

          if (addressInputState === 'error') {
            displayBackendErrorMessage({ name: 'Error', message: 'Invalid address' });
            updateField(fieldName, '');
          }
        }
      }
    }

    const manualAddressInputValidationResults = await Promise.all(manualAddressInputValidations);

    manualAddressInputValidationResults.forEach((isValid) => {
      if (!isValid) {
        isAnyManualInputAddressHasErrors = true;
      } else {
        isAnyManualInputAddressEntered = true;
      }
    });

    await formik.submitForm();
    const errors = await formik.validateForm();

    if (!isEmpty(errors)) {
      await formik.setTouched(
        {
          ...formik.touched,
          ...Object.keys(errors).reduce((acc, key) => {
            acc[key] = true;

            return acc;
          }, {}),
        },
        false,
      );

      return false;
    }

    if (isAnyManualInputAddressHasErrors) {
      return false;
    }

    const locAddressDestructured = parseLocation(state.loc_address);
    const mailAddressDestructured = showLocationAddressInput
      ? parseLocation(state.mail_address)
      : undefined;

    let data: ILocation = {
      ...state,
    };

    const locationExposureData = locationExposure?.data;
    const isLocAddressDifferent = locationExposureData?.loc_address !== state.loc_address;
    const isMailAddressDifferent =
      (locationExposureData?.mail_address || '') !== state.mail_address;

    const updateDataWithValidatedAddress = async (
      addressType: 'loc' | 'mail',
      addressDestructured: DestructuredAddress,
    ) => {
      const validatedAddress = await validateAddress(addressDestructured);

      data = {
        ...data,
        [`${addressType}_address_destructured`]: {
          ...addressDestructured,
          ...validatedAddress,
        },
      };
    };

    if (isAnyManualInputAddressEntered) {
      try {
        const loc_address_info = parseLocation(state?.loc_address);
        const mail_address_info = parseLocation(state?.mail_address);

        const body: ManuallyEnteredAddressPayload = {
          address_line_1: loc_address_info.street_line,
          ...(loc_address_info.secondary && { address_line_2: loc_address_info.secondary }),
          city: loc_address_info.city,
          loc_address_same_as_mailing_address: state?.loc_address_same_as_mailing_address as string,
          ...(showLocationAddressInput && {
            mailing_address_line_1: mail_address_info.street_line,
            ...(mail_address_info.secondary && {
              mailing_address_line_2: mail_address_info.secondary,
            }),
            mailing_city: mail_address_info.city,
            mailing_state: mail_address_info.state,
            mailing_zip_code: mail_address_info.zipcode,
          }),
          state: loc_address_info.state,
          zip_code: loc_address_info.zipcode,
        };

        setFullscreenLoading(true, 'Saving the address');

        if (!isEmpty(locationExposure)) {
          await updateManuallyEnteredLocationExposure({
            submissionId,
            body,
          });
        } else {
          await addManuallyEnteredLocationExposure({
            submissionId,
            body,
          });
        }

        refetchSubmission();

        return true;
      } catch (e) {
        displayBackendErrorMessage(e);

        return false;
      } finally {
        setFullscreenLoading(false);
      }
    }

    // Only validate changed or newly added addresses
    if (isLocAddressDifferent || isMailAddressDifferent) {
      try {
        setFullscreenLoading(true, 'Validating the address');

        if (isLocAddressDifferent && isMailAddressDifferent && !isEmpty(mailAddressDestructured)) {
          const [locAddressLatLong, mailAddressLatLong] = await Promise.all([
            validateAddress(locAddressDestructured),
            validateAddress(mailAddressDestructured),
          ]);

          data = {
            ...data,
            loc_address_destructured: {
              ...locAddressDestructured,
              ...locAddressLatLong,
            },
            mail_address_destructured: {
              ...mailAddressDestructured,
              ...mailAddressLatLong,
            },
          };
        } else {
          if (isLocAddressDifferent) {
            await updateDataWithValidatedAddress('loc', locAddressDestructured);
          }

          if (isMailAddressDifferent && !isEmpty(mailAddressDestructured)) {
            await updateDataWithValidatedAddress('mail', mailAddressDestructured);
          }
        }

        const loadingText = 'Saving the address';

        if (!isEmpty(locationExposure)) {
          await performAction({
            action: 'update',
            data,
            resourceId: locationExposure.id,
            loadingText,
            displayBEMessage: false,
          });
        } else {
          await performAction({
            action: 'add',
            data,
            loadingText,
            displayBEMessage: false,
          });
        }
      } catch (err) {
        displayBackendErrorMessage(err);
        return false;
      } finally {
        setFullscreenLoading(false);
      }
    }

    return true;
  };

  const renderSmarty = (questionText: string, fieldName: 'loc_address' | 'mail_address') => (
    <MuiFormControl>
      <Typography variant="body1" mb={1.2}>
        {questionText}
      </Typography>

      <SmartyAutocompleteInput
        fieldName={fieldName}
        formik={formik}
        showCantFindMyAddressButton
        state={state}
        ref={(el) => {
          if (el) {
            smartyAutoCompleteInputRefs.current[fieldName] = el;
          } else {
            delete smartyAutoCompleteInputRefs.current[fieldName];
          }
        }}
        setState={setState}
        placeholder="Street, City, State, ZIP Code"
        includedStates={
          fieldName === 'mail_address' ? Object.values(STATES) : [submission?.us_state as string]
        }
      />
    </MuiFormControl>
  );

  return (
    <ExposuresWrapper title="About your location" onNext={handleNext} renderAddButton={false}>
      <form
        id="checkEligibilityResultForm"
        name="checkEligibilityResultForm"
        noValidate
        onSubmit={formik.handleSubmit}
      >
        <Stack gap={4.5}>
          {renderSmarty('Where is your business located?', 'loc_address')}

          <FormControl
            error={
              formik.touched.loc_address_same_as_mailing_address &&
              Boolean(formik.errors.loc_address_same_as_mailing_address)
            }
            required
            errorMessage={formik.errors.loc_address_same_as_mailing_address}
            field={{
              name: 'loc_address_same_as_mailing_address',
              code: 'loc_address_same_as_mailing_address',
            }}
          >
            <Typography variant="body1" mb={1.2}>
              Is this location the same as your mailing address?
            </Typography>

            <ToggleButtonsGroup
              options={DEFAULT_TOGGLE_BUTTONS_GROUP_OPTIONS}
              onChange={handleOnChangeToggleButtonGroups}
              value={formik.values.loc_address_same_as_mailing_address}
              testPrefix="loc_address_same_as_mailing_address"
            />
          </FormControl>

          {showLocationAddressInput &&
            renderSmarty('What is your mailing address?', 'mail_address')}
        </Stack>
      </form>
    </ExposuresWrapper>
  );
};

export default Location;
